import { AxiosError } from "axios";
import { QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "react-query";
import { GenericErrorResponse } from "../typings";
import { useApiErrorHandlers } from "./useApiErrorHandlers";

export type UseCustomQueryReturn<TData, TError> = UseQueryResult<TData, TError> & {
  /**
   * Customized error message
   */
   customErrorMsg: string;

   /**
    * Indicate the api is authorized
    */
   authorized: boolean;
}

/**
 * A wrapper to wrap React-Query useQuery hook to provide centralized error handling
 * @param key useQuery key
 * @param queryFn useQuery function to call api
 * @param options useQuery options
 * @param validateSession Indicate the query is session guarded
 * @param toRedirect Indicate the query should allow redirect handling if error occurs
 * @returns
 */
export function useCustomQuery<TQueryFnData = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
  key: TQueryKey,
  queryFn: QueryFunction<TQueryFnData>,
  options?: UseQueryOptions<TQueryFnData, AxiosError<GenericErrorResponse>, TData>,
  validateSession = true,
  toRedirect = true
): UseCustomQueryReturn<TData, AxiosError<GenericErrorResponse>> {
  const { sessionErrorHandler, apiErrorHandler} = useApiErrorHandlers();
  const res = useQuery<TQueryFnData, AxiosError<GenericErrorResponse>, TData, TQueryKey>(
    key,
    queryFn,
    { ...options } as Omit<UseQueryOptions<TQueryFnData, AxiosError<GenericErrorResponse>, TData, TQueryKey>, 'queryKey' | 'queryFn'>
  );

  return {
    ...res,
    authorized: validateSession ? sessionErrorHandler(res.error, toRedirect) : true,
    customErrorMsg: apiErrorHandler(res.error, toRedirect),
  }
}
